/* eslint-disable no-nested-ternary */
import ProgressBar from '@brands/Components/ProgressBar/ProgressBar';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import { selectAuth } from '@brands/store/selectors/auth';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { getFormatDateFullDate } from '@brands/Utils/formatDate';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { Button } from '@mui/material';
import React, { FC } from 'react';
import Modal, { Styles } from 'react-modal';

import closeICon from '../../Patient/components/closeIconSVG.svg';
import styles from './styles.module.scss';

interface OpenCaseListModalProps {
  openCases: ICase[];
  isOpen: string | null;
  toggleModal: (value: string | null) => void;
  startPatientForm?: (getCases?: boolean) => Promise<void>;
  startOneReport?: (getCases?: boolean) => Promise<void>;
  startSecondOpinionCase?: (getCases?: boolean) => Promise<void>;
  handleOptionClick?: (
    organizationId: string,
    option: {
      selectedCaseType: string;
      selectedCasePrice: string;
    },
    getCases?: boolean,
  ) => Promise<void>;
  startCareCoordinationCase?: (getCases?: boolean) => Promise<void>;
  startOralHealthCoachingCase?: (getCases?: boolean) => Promise<void>;
  startClinicalEnablementCase?: (getCases?: boolean) => Promise<void>;
  orgId?: string;
  selectedCase: {
    selectedCaseType: string;
    selectedCasePrice: string;
  };
}

const mobileCustomStylesDashboard: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...(customStylesDashboard?.overlay || {}),
    overflowY: 'scroll' as const,
    overflowX: 'hidden' as const,
  },
  content: {
    ...(customStylesDashboard?.content || {}),
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    right: '0',
    bottom: '0',
    padding: '90px 16px 32px 16px',
    margin: '0',
    borderRadius: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
};

const customStylesDashboardDesktop = {
  ...customStylesDashboard,
  content: {
    ...customStylesDashboard.content,
    width: '444px',
  },
};
const OpenCaseListModal: FC<OpenCaseListModalProps> = ({
  openCases,
  isOpen,
  toggleModal,
  startPatientForm,
  startOneReport,
  startSecondOpinionCase,
  startCareCoordinationCase,
  startOralHealthCoachingCase,
  startClinicalEnablementCase,
  handleOptionClick,
  orgId,
  selectedCase,
}) => {
  const roleNavigate = useRoleNavigate();
  const isMobile = useWindowSize().width < 992;
  const { userInfo } = useAppSelector(selectAuth);

  const closeModal = (): void => {
    toggleModal(null);
  };

  const SYNC_CASES = ['video_call_scheduled', 'video_cal_instant', 'phone_call_scheduled'];

  const continueModal = async (): Promise<void> => {
    const caseType = isOpen;
    toggleModal(null);
    // Clinical Enablement is offered by this brand only, so it is handled
    // ahead of the shared case types rather than joining the group check
    // below — a caller without a Clinical Enablement entry point must still
    // be able to continue into the case types it does provide.
    if (caseType === CaseType.clinical_enablement) {
      if (startClinicalEnablementCase) {
        await startClinicalEnablementCase(false);
        return;
      }
      if (handleOptionClick) {
        await handleOptionClick(
          orgId || '',
          {
            selectedCaseType: selectedCase.selectedCaseType || '',
            selectedCasePrice: selectedCase.selectedCasePrice,
          },
          false,
        );
        return;
      }
      displayErrorDetails('No function provided to continue modal');
      return;
    }
    if (
      startPatientForm &&
      startOneReport &&
      startSecondOpinionCase &&
      startCareCoordinationCase &&
      startOralHealthCoachingCase
    ) {
      switch (caseType) {
        case 'video_call_scheduled':
          await startPatientForm(false);
          break;
        case 'one_report':
          await startOneReport(false);
          break;
        case 'second_opinion':
          await startSecondOpinionCase(false);
          break;
        case CaseType.care_coordination:
          await startCareCoordinationCase(false);
          break;
        case CaseType.oral_health_coaching:
          await startOralHealthCoachingCase(false);
          break;
        default:
          break;
      }
    } else if (handleOptionClick) {
      await handleOptionClick(
        orgId || '',
        {
          selectedCaseType: selectedCase.selectedCaseType || '',
          selectedCasePrice: selectedCase.selectedCasePrice,
        },
        false,
      );
    } else {
      displayErrorDetails('No function provided to continue modal');
    }
  };

  const viewCase = (caseId: number, patientId: number): void => {
    if (userInfo.role.name === 'Patient') {
      const isOwnCase = patientId === userInfo.id;
      roleNavigate(
        isOwnCase ? '/my-care#upcoming_appointments' : `/my-care?patientId=${patientId}#upcoming_appointments`,
      );
    } else {
      roleNavigate(`/consultation-page/${caseId}`);
    }
    toggleModal(null);
  };

  const getConsultationMessage = (): string => {
    const { length: openCaseCount } = openCases;
    const isPatient = userInfo.role.name === 'Patient';

    if (openCaseCount > 1) {
      return isPatient
        ? 'It looks like you have active consultations'
        : 'It looks like the patient has active consultations';
    }

    if (openCaseCount === 1) {
      return isPatient
        ? 'It looks like you have an active consultation'
        : 'It looks like the patient has an active consultation';
    }

    return '';
  };

  const renderOpenCases = (cases: ICase[]): JSX.Element => {
    return (
      <div className={`fs-unmask ${styles.openCasesCaseContainer}`}>
        {cases.map((item) => {
          const date = SYNC_CASES.includes(item.type) ? item.video_conference_date || '' : item.created_at;
          const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
          return (
            <div key={item.id} className={`fs-unmask ${styles.openCasesCaseBody}`}>
              <div className={`fs-unmask ${styles.openCasesCaseContent}`}>
                <span className={`fs-unmask ${styles.openCasesType}`}>
                  {getFriendlyName(item.type)} consultation with{' '}
                  <span className="fs-exclude">
                    {item.provider
                      ? `${item.provider.full_name}, ${item.provider.provider_profile.credentials}`
                      : item.care_advisor
                        ? item.care_advisor.full_name
                        : ''}{' '}
                  </span>
                </span>
                <span className={`fs-unmask ${styles.openCaseDate}`}>{`${getFormatDateFullDate(date, timeZone)}`}</span>

                <Button
                  type="button"
                  className="fs-unmask"
                  variant="text"
                  onClick={() => viewCase(item.id, item.patient_id)}
                  style={{ padding: '0' }}
                >
                  View/Edit
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      isOpen={typeof isOpen === 'string'}
      onRequestClose={closeModal}
      style={isMobile ? mobileCustomStylesDashboard : customStylesDashboardDesktop}
      contentLabel="Modal"
      ariaHideApp={false}
    >
      {isMobile ? (
        <div className={styles.backDiv}>
          <ProgressBar currentStep={1} totalSteps={1} onBack={closeModal} hideStepNumbers />
        </div>
      ) : (
        <div className={styles.closeICon}>
          <img src={closeICon} onClick={() => closeModal()} alt="Close modal" />
        </div>
      )}
      {openCases && openCases.length > 0 && (
        <div className={styles.openCasesContainer}>
          <div className={`fs-unmask ${styles.openCasesTitle}`}>{getConsultationMessage()}</div>
          <div className={`fs-unmask ${styles.openCasesBody}`}>{renderOpenCases(openCases)}</div>
          <Button
            type="button"
            variant="contained"
            data-testid="open-case-list-create-new-consultation"
            className={`fs-unmask ${styles.openCasesCreateButton}`}
            onClick={continueModal}
          >
            Create New Consultation
          </Button>
          {userInfo.role.name === 'Patient' && <hr />}
          {userInfo.role.name === 'Patient' && (
            <div className={styles.openCasesFooter}>
              <div className={`fs-unmask ${styles.openCasesFooterPrimary}`}>Need help?</div>
              <div className={`fs-unmask ${styles.openCasesFooterSecondary}`}>
                Give us a call at <a href="tel:1-800-123-4567">+1 (877) 712-7875</a> to speak with a care advisor
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

Modal.setAppElement('#root');

export default OpenCaseListModal;
